import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { useIsModerator } from "@/hooks/useAdminReports";
import { ReactNode, useState, useEffect, useMemo, lazy, Suspense } from "react";
import { SplashScreen } from "@/components/SplashScreen";
import { Onboarding } from "@/components/Onboarding";
import { CookieConsent } from "@/components/CookieConsent";
import { PageTransition } from "@/components/PageTransition";
import { addAppUrlListener, getAppScheme } from "@/lib/native";
import Index from "./pages/Index";

// Retry wrapper for lazy imports – handles transient fetch failures (stale hashes after deploy/HMR)
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
): Promise<{ default: T }> {
  return factory().catch((err) => {
    if (retries > 0) {
      return new Promise<{ default: T }>((resolve) =>
        setTimeout(() => resolve(lazyRetry(factory, retries - 1)), 1000),
      );
    }
    // After retries exhausted, force reload to get fresh assets
    window.location.reload();
    throw err;
  });
}

// Lazy load pages for code splitting - reduces initial bundle size
// Keep auth-critical routes eager to avoid blank screens from transient module fetch failures.
const CreateProfilePage = lazy(() => lazyRetry(() => import("./pages/CreateProfile").then(m => ({ default: m.CreateProfilePage }))));
const DiscoverPage = lazy(() => lazyRetry(() => import("./pages/Discover").then(m => ({ default: m.DiscoverPage }))));
const MatchesPage = lazy(() => lazyRetry(() => import("./pages/Matches").then(m => ({ default: m.MatchesPage }))));
const MessagesPage = lazy(() => lazyRetry(() => import("./pages/Messages").then(m => ({ default: m.MessagesPage }))));
const ChatPage = lazy(() => lazyRetry(() => import("./pages/Chat").then(m => ({ default: m.ChatPage }))));
const ProfilePage = lazy(() => lazyRetry(() => import("./pages/Profile").then(m => ({ default: m.ProfilePage }))));
const ViewProfilePage = lazy(() => lazyRetry(() => import("./pages/ViewProfile").then(m => ({ default: m.ViewProfilePage }))));
const SafetyTipsPage = lazy(() => lazyRetry(() => import("./pages/SafetyTips").then(m => ({ default: m.SafetyTipsPage }))));
const PrivacyPolicyPage = lazy(() => lazyRetry(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicyPage }))));
const TermsOfServicePage = lazy(() => lazyRetry(() => import("./pages/TermsOfService").then(m => ({ default: m.TermsOfServicePage }))));
const SmashOrPassPage = lazy(() => lazyRetry(() => import("./pages/SmashOrPass").then(m => ({ default: m.SmashOrPassPage }))));
const AdminPage = lazy(() => lazyRetry(() => import("./pages/Admin").then(m => ({ default: m.AdminPage }))));
const DeleteAccountPage = lazy(() => lazyRetry(() => import("./pages/DeleteAccount").then(m => ({ default: m.DeleteAccountPage }))));
const AuthPage = lazy(() => lazyRetry(() => import("./pages/Auth").then(m => ({ default: m.AuthPage }))));
const ResetPasswordPage = lazy(() => lazyRetry(() => import("./pages/ResetPassword").then(m => ({ default: m.ResetPasswordPage }))));
const NotFound = lazy(() => lazyRetry(() => import("./pages/NotFound")));
const WhatIsSmashOrPassPage = lazy(() => lazyRetry(() => import("./pages/WhatIsSmashOrPass").then(m => ({ default: m.WhatIsSmashOrPassPage }))));
const SmashOrPassVsTinderPage = lazy(() => lazyRetry(() => import("./pages/SmashOrPassVsTinder").then(m => ({ default: m.SmashOrPassVsTinderPage }))));
const SmashOrPassUKPage = lazy(() => lazyRetry(() => import("./pages/SmashOrPassUK").then(m => ({ default: m.SmashOrPassUKPage }))));
const SmashOrPassUSPage = lazy(() => lazyRetry(() => import("./pages/SmashOrPassUS").then(m => ({ default: m.SmashOrPassUSPage }))));
const DatingProfileTipsPage = lazy(() => lazyRetry(() => import("./pages/DatingProfileTips").then(m => ({ default: m.DatingProfileTipsPage }))));
const GuidesPage = lazy(() => lazyRetry(() => import("./pages/Guides").then(m => ({ default: m.GuidesPage }))));
const FirstMessageGuidePage = lazy(() => lazyRetry(() => import("./pages/FirstMessageGuide").then(m => ({ default: m.FirstMessageGuidePage }))));
const AttachmentStylesPage = lazy(() => lazyRetry(() => import("./pages/AttachmentStyles").then(m => ({ default: m.AttachmentStylesPage }))));
const FAQPage = lazy(() => lazyRetry(() => import("./pages/FAQ").then(m => ({ default: m.FAQPage }))));
const PokemonSmashOrPassPage = lazy(() => lazyRetry(() => import("./pages/PokemonSmashOrPass").then(m => ({ default: m.PokemonSmashOrPassPage }))));
const AnimeSmashOrPassPage = lazy(() => lazyRetry(() => import("./pages/AnimeSmashOrPass").then(m => ({ default: m.AnimeSmashOrPassPage }))));
const GamesPage = lazy(() => lazyRetry(() => import("./pages/Games").then(m => ({ default: m.GamesPage }))));
const TicTacToePage = lazy(() => lazyRetry(() => import("./pages/games/TicTacToe").then(m => ({ default: m.TicTacToePage }))));
const RockPaperScissorsPage = lazy(() => lazyRetry(() => import("./pages/games/RockPaperScissors").then(m => ({ default: m.RockPaperScissorsPage }))));
const ConnectFourPage = lazy(() => lazyRetry(() => import("./pages/games/ConnectFour").then(m => ({ default: m.ConnectFourPage }))));
const CompatibilityTestPage = lazy(() => lazyRetry(() => import("./pages/CompatibilityTest").then(m => ({ default: m.CompatibilityTestPage }))));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,     // 2 min — data considered fresh, no refetch on mount/focus
      gcTime: 10 * 60 * 1000,        // 10 min — keep inactive cache before garbage collection
      refetchOnWindowFocus: false,    // prevent refetch storm when user tabs back
      retry: 1,                       // single retry on failure
    },
  },
});

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Moderator-only route wrapper
const ModeratorRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  const { data: isModerator, isLoading: modLoading } = useIsModerator();

  if (loading || modLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isModerator) {
    return <Navigate to="/discover" replace />;
  }

  return <>{children}</>;
};

// Public route - redirect if logged in
const PublicRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/discover" replace />;
  }

  return <>{children}</>;
};

// Deep link handler component
const DeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const scheme = getAppScheme();

    const removeListener = addAppUrlListener((url) => {
      // Parse the deep link URL
      // Format: app.smashorpassdating://path?params
      if (!url.startsWith(scheme)) return;

      const path = url.replace(`${scheme}://`, '');
      const [route, queryString] = path.split('?');
      const fullPath = queryString ? `${route}?${queryString}` : route;

      // Skip OAuth callback links so the auth SDK can complete sign-in.
      // These contain params like code=, access_token=, error=, or error_description=.
      const isAuthCallback =
        fullPath.includes('code=') ||
        fullPath.includes('access_token=') ||
        fullPath.includes('error=') ||
        fullPath.includes('error_description=');

      if (isAuthCallback) {
        return;
      }

      if (route) {
        // Navigate to the route with query params
        navigate(queryString ? `/${route}?${queryString}` : `/${route}`);
      }
    });

    return removeListener;
  }, [navigate]);

  return null;
};

// Minimal loading fallback for lazy routes
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

const AppRoutes = () => {
  return (
    <>
      <DeepLinkHandler />
      <Suspense fallback={<RouteLoader />}>
        <PageTransition>
          <Routes>
            <Route path="/" element={<PublicRoute><Index /></PublicRoute>} />
            <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/profile/create" element={<ProtectedRoute><CreateProfilePage /></ProtectedRoute>} />
            <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
            <Route path="/matches" element={<ProtectedRoute><MatchesPage /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/chat/:matchId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/profile/:profileId" element={<ProtectedRoute><ViewProfilePage /></ProtectedRoute>} />
            <Route path="/smash-or-pass" element={<ProtectedRoute><SmashOrPassPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ModeratorRoute><AdminPage /></ModeratorRoute>} />
            <Route path="/safety-tips" element={<SafetyTipsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/what-is-smash-or-pass" element={<WhatIsSmashOrPassPage />} />
            <Route path="/smash-or-pass-vs-tinder" element={<SmashOrPassVsTinderPage />} />
            <Route path="/smash-or-pass-uk" element={<SmashOrPassUKPage />} />
            <Route path="/smash-or-pass-us" element={<SmashOrPassUSPage />} />
            <Route path="/dating-profile-tips" element={<DatingProfileTipsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/first-message-guide" element={<FirstMessageGuidePage />} />
            <Route path="/attachment-styles" element={<AttachmentStylesPage />} />
            <Route path="/game/pokemon-smash-or-pass" element={<PokemonSmashOrPassPage />} />
            <Route path="/game/anime-smash-or-pass" element={<AnimeSmashOrPassPage />} />
            <Route path="/games" element={<GamesPage />} />
            <Route path="/game/tic-tac-toe" element={<TicTacToePage />} />
            <Route path="/game/rock-paper-scissors" element={<RockPaperScissorsPage />} />
            <Route path="/game/connect-four" element={<ConnectFourPage />} />
            <Route path="/compatibility-test" element={<ProtectedRoute><CompatibilityTestPage /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </Suspense>
    </>
  );
};

// Inner app component that can access auth context
const AppContent = () => {
  const { user, loading: authLoading } = useAuth();
  // Skip splash on public marketing/quiz/legal routes — they don't need auth
  // and the splash overlay was hiding the content (e.g. /game/* quizzes).
  const PUBLIC_ROUTE_PREFIXES = ['/game/', '/games', '/what-is-smash-or-pass', '/smash-or-pass-vs-tinder', '/smash-or-pass-uk', '/smash-or-pass-us', '/dating-profile-tips', '/faq', '/guides', '/first-message-guide', '/attachment-styles', '/safety-tips', '/privacy-policy', '/terms-of-service', '/delete-account'];
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isLandingRoute = pathname === '/';
  const isPublicRoute = isLandingRoute || PUBLIC_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
  const splashAlreadyShown = typeof window !== 'undefined' && sessionStorage.getItem('splashShown');
  const [showSplash, setShowSplash] = useState(!isPublicRoute && !splashAlreadyShown);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [appReady, setAppReady] = useState(false);

  // Track when app is fully initialized
  useEffect(() => {
    if (!authLoading) {
      setAppReady(true);
    }
  }, [authLoading]);

  // If we skipped the splash on a public route, mark it shown so subsequent navigation doesn't trigger it
  useEffect(() => {
    if (isPublicRoute && !splashAlreadyShown) {
      sessionStorage.setItem('splashShown', 'true');
    }
  }, [isPublicRoute, splashAlreadyShown]);

  const loadingSteps = useMemo(() => [
    { label: "Checking authentication...", completed: !authLoading },
    { label: "Initializing app...", completed: appReady },
  ], [authLoading, appReady]);

  const handleSplashComplete = () => {
    sessionStorage.setItem("splashShown", "true");
    setShowSplash(false);
    
    // Check if user has seen onboarding
    const onboardingComplete = localStorage.getItem("onboardingComplete");
    if (!onboardingComplete) {
      setShowOnboarding(true);
    }
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem("onboardingComplete", "true");
    setShowOnboarding(false);
  };

  return (
    <>
      {showSplash && (
        <SplashScreen 
          onComplete={handleSplashComplete} 
          loadingSteps={loadingSteps}
        />
      )}
      {showOnboarding && (
        <Onboarding onComplete={handleOnboardingComplete} />
      )}
      <Toaster position="top-center" richColors />
      <CookieConsent />
      <AppRoutes />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <AppContent />
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
